import React from 'react';
import {
  TripMatch, ConversationSummaries, ConversationType, TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { ConversationLinkProps } from './ConversationLink';
import { getConversationSummaryByConversationId, getConversationSummaryByConversationType } from './helpers';
import ConversationLinksCard, { ConversationLinksCardProps } from './ConversationLinksCard';
import { ConversationLinkMap } from './types';

type TripMatchConversationLinksProps = {
  tripMatchId: string;
  renderConversationLink: ConversationLinksCardProps['renderConversationLink'];
  getConversationTypeTitle: ConversationLinkProps['getConversationTypeTitle'];
};

const TripMatchConversationLinks = ({
  tripMatchId,
  renderConversationLink,
  getConversationTypeTitle,
}: TripMatchConversationLinksProps) => {
  const apiClient = useApiClient();

  // Fetch all required data upfront
  const {
    data: tripMatch,
    error: tripMatchError,
    isLoading: isTripMatchLoading,
  } = useFetch<TripMatch>(
    apiClient.tripMatchesClient.getTripMatch,
    [tripMatchId],
    !!tripMatchId,
  );

  const {
    data: tripRequest,
    error: tripRequestError,
    isLoading: isTripRequestLoading,
  } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [tripMatch?.tripRequestId],
    !!tripMatch?.tripRequestId,
  );

  const {
    data: allConversations,
    error: allConversationsError,
    isLoading: isAllConversationsLoading,
  } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripMatch?.tripRequestId],
    !!tripMatch?.tripRequestId,
  );

  const {
    data: assignedTeamMemberConversations,
    error: assignedTeamMemberError,
    isLoading: isAssignedTeamMemberLoading,
  } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripMatch?.tripRequestId, { params: { person: tripRequest?.assignedTo } }],
    !!(tripMatch?.tripRequestId && tripRequest?.assignedTo),
  );

  const {
    data: assignedAgentConversations,
    error: assignedAgentError,
    isLoading: isAssignedAgentLoading,
  } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripMatch?.tripRequestId, { params: { person: tripMatch?.assignedTo } }],
    !!(tripMatch?.tripRequestId && tripMatch?.assignedTo),
  );

  // Handle loading state
  const isLoading = isTripMatchLoading
    || isTripRequestLoading
    || isAllConversationsLoading
    || isAssignedAgentLoading
    || isAssignedTeamMemberLoading;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Handle error state
  const hasError = tripMatchError
    || tripRequestError
    || allConversationsError
    || assignedAgentError
    || assignedTeamMemberError
    || !allConversations
    || !tripMatch
    || !tripRequest;

  if (hasError) {
    return <div>Error loading conversation data</div>;
  }

  // Get default conversations
  const defaultTravelerToZicasso = getConversationSummaryByConversationId(
    tripMatch.travelerToZicassoId,
    allConversations,
  );
  const defaultTravelerToPartner = getConversationSummaryByConversationId(
    tripMatch.travelerToPartnerId,
    allConversations,
  );
  const defaultZicassoToPartner = getConversationSummaryByConversationId(
    tripMatch.zicassoToPartnerId,
    allConversations,
  );

  if (!defaultTravelerToPartner || !defaultZicassoToPartner) {
    return <div>Error loading conversation data</div>;
  }

  const summaries: ConversationLinkMap = {};

  // Only the oryx app will have a defaultTravelerToZicasso conversation
  if (defaultTravelerToZicasso) {
    summaries[ConversationType.TravelerToZicasso] = {
      ...(tripRequest.assignedTo && assignedTeamMemberConversations && {
        teamMemberContext: getConversationSummaryByConversationType(
          ConversationType.TravelerToZicasso,
          assignedTeamMemberConversations,
        )!,
      }),
      defaultContext: defaultTravelerToZicasso,
    };
  }

  // Build conversation summaries
  summaries[ConversationType.TravelerToPartner] = {
    ...(tripMatch.assignedTo && assignedAgentConversations && {
      agentContext: getConversationSummaryByConversationId(
        tripMatch.travelerToPartnerId,
        assignedAgentConversations,
      ),
    }),
    defaultContext: defaultTravelerToPartner,
  };
  summaries[ConversationType.ZicassoToPartner] = {
    ...(tripMatch.assignedTo && assignedAgentConversations && {
      agentContext: getConversationSummaryByConversationId(
        tripMatch.zicassoToPartnerId,
        assignedAgentConversations,
      ),
    }),
    ...(tripRequest.assignedTo && assignedTeamMemberConversations && {
      teamMemberContext: getConversationSummaryByConversationId(
        tripMatch.zicassoToPartnerId,
        assignedTeamMemberConversations,
      ),
    }),
    defaultContext: defaultZicassoToPartner,
  };

  return (
    <ConversationLinksCard
      key={tripMatch.id}
      conversationSummaries={summaries}
      tripMatchId={tripMatchId}
      tripRequestId={tripMatch.tripRequestId}
      renderConversationLink={renderConversationLink}
      getConversationTypeTitle={getConversationTypeTitle}
    />
  );
};

export default TripMatchConversationLinks;
